// Renders the sentence the LLM thought defined this term, with the term
// wrapped in the same <mark class="maisight-term-mark"> markup the renderer
// uses. This is the killer-UX bit: the customer sees exactly what their
// reader will see, in context, without leaving the review screen.

import type { Block, DocJSON, GlossaryEntry } from "../data/types";

interface Props {
  doc: DocJSON;
  entry: GlossaryEntry;
}

function joinBlockText(block: Block): string {
  if (!block.inline) return block.raw ?? "";
  return block.inline.map((r) => r.text ?? "").join("");
}

// Find the sentence in `text` that contains the term match at offset.
// Falls back to the whole block if sentence splitting fails.
function sentenceAround(text: string, idx: number, len: number): { start: number; end: number } {
  const stops = /[.!?]\s+/g;
  let lastEnd = 0;
  let m: RegExpExecArray | null;
  // Walk forward to find the sentence end after idx.
  let sentEnd = text.length;
  let sentStart = 0;
  while ((m = stops.exec(text)) !== null) {
    const stopAt = m.index + 1; // include the punctuation
    if (m.index < idx) {
      sentStart = stopAt + (m[0].length - 1); // start of next sentence
    } else if (stopAt >= idx + len) {
      sentEnd = stopAt;
      break;
    }
    lastEnd = stopAt;
  }
  if (sentEnd <= sentStart) sentEnd = Math.min(text.length, lastEnd || text.length);
  // Trim leading whitespace from the sentence start.
  while (sentStart < text.length && /\s/.test(text[sentStart] ?? "")) sentStart++;
  return { start: sentStart, end: sentEnd };
}

export function SourceSpan({ doc, entry }: Props) {
  if (!entry.source_block_id) return null;
  const block = doc.sections
    .flatMap((s) => s.blocks)
    .find((b) => b.id === entry.source_block_id);
  if (!block) return null;

  const text = joinBlockText(block);
  if (!text) return null;

  // Prefer the explicit source_offset/length if provided; otherwise locate
  // the first case-insensitive match of `term` in the block.
  let idx = entry.source_offset ?? -1;
  let len = entry.source_length ?? entry.term.length;
  if (idx < 0) {
    const found = text.toLowerCase().indexOf(entry.term.toLowerCase());
    if (found < 0) {
      // Render the whole block as fallback.
      return (
        <blockquote className="maisight-review-source-span">
          <span className="maisight-review-source-span-text">{text}</span>
        </blockquote>
      );
    }
    idx = found;
  }

  const { start, end } = sentenceAround(text, idx, len);
  const before = text.slice(start, idx);
  const hit = text.slice(idx, idx + len);
  const after = text.slice(idx + len, end);

  return (
    <blockquote className="maisight-review-source-span" data-source-block-id={block.id}>
      <span className="maisight-review-source-span-text">
        {before}
        <mark
          className="maisight-term-mark is-lit"
          data-term={entry.term}
          data-term-id={entry.term_id}
        >
          {hit}
        </mark>
        {after}
      </span>
    </blockquote>
  );
}
